/**
 * Local card images. The app never loads card images from Scryfall: images
 * are downloaded once by `npm run data:download-images` into
 * public/cards/images/<printingId>.jpg (back faces: <printingId>-back.jpg)
 * and served by the app itself. Missing images fall back to a placeholder.
 */

import type { ScryfallCard, ScryfallImageUris } from "./scryfall";

export type CardImageFace = "front" | "back";

const IMAGE_DIR = `${import.meta.env.BASE_URL}cards/images/`;

/** Shown when a card has no local image (not downloaded yet). */
export const CARD_IMAGE_PLACEHOLDER = `${import.meta.env.BASE_URL}cards/card-back.svg`;

const SCRYFALL_IMAGE = /\/(front|back)\/[0-9a-f]\/[0-9a-f]\/([0-9a-f-]{36})\.jpg/i;
const LOCAL_IMAGE = /\/cards\/images\/[0-9a-f-]+(?:-back)?\.jpg$/i;

/** URL of the local image of a printing, e.g. /cards/images/<uuid>.jpg. */
export function getCardImagePath(printingId: string, face: CardImageFace = "front"): string {
  return imagePathForKey(face === "back" ? `${printingId}-back` : printingId);
}

/** URL for an image key as written into the card bundle (`image_key`). */
export function imagePathForKey(key: string): string {
  return `${IMAGE_DIR}${key.toLowerCase()}.jpg`;
}

/** Image key (`<printingId>` or `<printingId>-back`) of a Scryfall image URL. */
export function imageKeyFromUri(uri: string | null | undefined): string | null {
  const match = uri ? SCRYFALL_IMAGE.exec(uri) : null;
  if (!match?.[1] || !match[2]) return null;
  const id = match[2].toLowerCase();
  return match[1].toLowerCase() === "back" ? `${id}-back` : id;
}

/**
 * Turn any stored image reference into a local URL. Scryfall image URLs (from
 * older caches, persisted decks or engine responses) map to the local copy;
 * other remote URLs are dropped so no card image is ever fetched online.
 */
export function localCardImage(src: string | null | undefined): string | null {
  if (!src) return null;
  if (LOCAL_IMAGE.test(src) || src.startsWith("data:") || src.startsWith("blob:")) return src;
  const key = imageKeyFromUri(src);
  return key === null ? null : imagePathForKey(key);
}

function keyFromUris(uris: ScryfallImageUris | undefined): string | null {
  return imageKeyFromUri(uris?.normal ?? uris?.large ?? uris?.small ?? uris?.art_crop);
}

/**
 * Local image for a card (front face by default). Bundle cards carry an
 * `image_key`; cards from other sources fall back to the printing ID in
 * their Scryfall image URL. Null when no local image can exist.
 */
export function cardImage(card: ScryfallCard, faceIndex = 0): string | null {
  const face = card.card_faces?.[faceIndex];
  const perFace = face?.image_key !== undefined || face?.image_uris !== undefined;
  if (faceIndex > 0 && !perFace) return null;

  const key =
    (perFace ? (face?.image_key ?? keyFromUris(face?.image_uris)) : null) ??
    card.image_key ??
    keyFromUris(card.image_uris);
  return key ? imagePathForKey(key) : null;
}

/** onError handler for <img>: swap to the placeholder once. */
export function showPlaceholderOnError(event: { currentTarget: HTMLImageElement }): void {
  const image = event.currentTarget;
  if (!image.src.endsWith(CARD_IMAGE_PLACEHOLDER)) {
    image.src = CARD_IMAGE_PLACEHOLDER;
  }
}
